import { CSElementPlugin } from '../types/plugin-interfaces';
import { CSElement } from '../core/CSElement';
import * as pako from 'pako';
import * as lz4 from 'lz4js';

// Определяем недостающие типы
export interface PluginContext {
    element: CSElement;
    operation: string;
    [key: string]: any;
}

export interface PluginResult {
    success: boolean;
    data?: any;
    error?: string;
    [key: string]: any;
}

// Типы сжатия
export enum CompressionAlgorithm {
    GZIP = 'gzip',
    DEFLATE = 'deflate',
    BROTLI = 'brotli',
    LZ4 = 'lz4',
    ZSTD = 'zstd',
    SNAPPY = 'snappy'
}

export enum CompressionLevel {
    FASTEST = 1,
    FAST = 3,
    BALANCED = 6,
    BEST = 9
}

export enum CompressionMode {
    AUTOMATIC = 'automatic',
    MANUAL = 'manual',
    THRESHOLD_BASED = 'threshold_based',
    SELECTIVE = 'selective'
}

export enum DataType {
    JSON = 'json',
    TEXT = 'text',
    BINARY = 'binary',
    IMAGE = 'image',
    AUDIO = 'audio',
    VIDEO = 'video'
}

// Интерфейсы сжатия
export interface CompressionResult {
    originalSize: number;
    compressedSize: number;
    compressionRatio: number;
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    duration: number;
    checksum: string;
    metadata: Record<string, any>;
}

export interface CompressionOptions {
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    dictionary?: Buffer;
    windowSize?: number;
    memoryLevel?: number;
    strategy?: number;
    chunkSize?: number;
    validateChecksum?: boolean;
}

export interface CompressionRule {
    id: string;
    name: string;
    description?: string;
    enabled: boolean;
    priority: number;
    conditions: CompressionCondition[];
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    options?: Partial<CompressionOptions>;
    createdAt: Date;
    updatedAt: Date;
}

export interface CompressionCondition {
    type: 'size' | 'dataType' | 'elementType' | 'custom';
    operator: 'greater' | 'less' | 'equals' | 'contains' | 'matches';
    value: any;
    field?: string;
}

export interface CompressionProfile {
    id: string;
    name: string;
    description?: string;
    dataTypes: DataType[];
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    options: CompressionOptions;
    isDefault: boolean;
    benchmarkResults?: BenchmarkResult[];
}

export interface BenchmarkResult {
    dataSize: number;
    dataType: DataType;
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    compressionRatio: number;
    compressionTime: number;
    decompressionTime: number;
    memoryUsage: number;
    timestamp: Date;
}

export interface CompressionJob {
    id: string;
    elementId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    startTime: Date;
    endTime?: Date;
    originalSize: number;
    compressedSize?: number;
    error?: string;
    result?: CompressionResult;
}

export interface CompressionCache {
    key: string;
    data: Buffer;
    algorithm: CompressionAlgorithm;
    level: CompressionLevel;
    checksum: string;
    createdAt: Date;
    lastAccessed: Date;
    accessCount: number;
    size: number;
}

// Конфигурация плагина
export interface CompressionPluginConfig {
    enabled?: boolean;
    priority?: number;
    defaultAlgorithm: CompressionAlgorithm;
    defaultLevel: CompressionLevel;
    mode: CompressionMode;
    autoCompress: boolean;
    compressionThreshold: number; // минимальный размер для сжатия (байты)
    maxCompressionJobs: number;
    enableCaching: boolean;
    cacheSize: number; // максимальный размер кэша (байты)
    cacheTTL: number; // время жизни кэша (миллисекунды)
    enableBenchmarking: boolean;
    validateChecksums: boolean;
    enableStatistics: boolean;
    blacklistedDataTypes: DataType[];
    whitelistedDataTypes: DataType[];
}

// Статистика сжатия
export interface CompressionStats {
    totalCompressions: number;
    totalDecompressions: number;
    totalOriginalSize: number;
    totalCompressedSize: number;
    averageCompressionRatio: number;
    averageCompressionTime: number;
    averageDecompressionTime: number;
    compressionsByAlgorithm: Record<CompressionAlgorithm, number>;
    compressionsByLevel: Record<CompressionLevel, number>;
    cacheHits: number;
    cacheMisses: number;
    cacheHitRatio: number;
    activeJobs: number;
    failedCompressions: number;
    savedSpace: number;
    lastCompressionTime?: Date;
    bestCompressionRatio: number;
    worstCompressionRatio: number;
}

export class CompressionPlugin implements CSElementPlugin {
    public readonly name = 'CompressionPlugin';
    public readonly version = '1.0.0';
    public readonly description = 'Плагин для сжатия данных и оптимизации памяти';

    private config: CompressionPluginConfig;
    private rules = new Map<string, CompressionRule>();
    private profiles = new Map<string, CompressionProfile>();
    private jobs = new Map<string, CompressionJob>();
    private cache = new Map<string, CompressionCache>();
    private stats: CompressionStats;
    private compressedElements = new Map<string, CompressionResult>();

    constructor(config: Partial<CompressionPluginConfig> = {}) {
        this.config = {
            enabled: true,
            priority: 150,
            defaultAlgorithm: CompressionAlgorithm.GZIP,
            defaultLevel: CompressionLevel.BALANCED,
            mode: CompressionMode.THRESHOLD_BASED,
            autoCompress: true,
            compressionThreshold: 1024, // 1KB
            maxCompressionJobs: 5,
            enableCaching: true,
            cacheSize: 50 * 1024 * 1024, // 50MB
            cacheTTL: 3600000, // 1 час
            enableBenchmarking: false,
            validateChecksums: true,
            enableStatistics: true,
            blacklistedDataTypes: [],
            whitelistedDataTypes: [],
            ...config
        };

        this.stats = {
            totalCompressions: 0,
            totalDecompressions: 0,
            totalOriginalSize: 0,
            totalCompressedSize: 0,
            averageCompressionRatio: 0,
            averageCompressionTime: 0,
            averageDecompressionTime: 0,
            compressionsByAlgorithm: {} as Record<CompressionAlgorithm, number>,
            compressionsByLevel: {} as Record<CompressionLevel, number>,
            cacheHits: 0,
            cacheMisses: 0,
            cacheHitRatio: 0,
            activeJobs: 0,
            failedCompressions: 0,
            savedSpace: 0,
            bestCompressionRatio: 0,
            worstCompressionRatio: 1
        };

        // Инициализация счетчиков
        Object.values(CompressionAlgorithm).forEach(algorithm => {
            this.stats.compressionsByAlgorithm[algorithm] = 0;
        });
        Object.values(CompressionLevel).forEach(level => {
            this.stats.compressionsByLevel[level as CompressionLevel] = 0;
        });

        this.initializeDefaultProfiles();
        this.initializeDefaultRules();
    }

    install(_CSElementClass: typeof CSElement): void {
        // Регистрация плагина в системе CSElement
        // В реальной реализации здесь была бы интеграция с жизненным циклом элементов
        console.log(`${this.name} установлен для CSElement`);
    }

    async initialize(): Promise<void> {
        console.log(`Инициализация ${this.name} v${this.version}`);
        
        // Очистка кэша по расписанию
        if (this.config.enableCaching) {
            setInterval(() => {
                this.cleanupCache();
            }, 60000); // каждую минуту
        }

        // Обновление статистики
        if (this.config.enableStatistics) {
            setInterval(() => {
                this.updateStats();
            }, 30000); // каждые 30 секунд
        }
    }

    async destroy(): Promise<void> {
        // Завершение активных задач
        for (const job of this.jobs.values()) {
            if (job.status === 'processing') {
                job.status = 'failed';
                job.error = 'Plugin shutdown';
                job.endTime = new Date();
            }
        }
        
        this.cache.clear();
        console.log(`${this.name} деинициализирован`);
    }

    getConfig(): CompressionPluginConfig {
        return { ...this.config };
    }

    updateConfig(newConfig: Partial<CompressionPluginConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    // Lifecycle hooks
    async afterCreate(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.config.autoCompress && this.shouldCompress(element)) {
            await this.compressElement(element);
        }
        return { success: true };
    }

    async afterUpdate(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.config.autoCompress && this.shouldCompress(element)) {
            await this.compressElement(element);
        }
        return { success: true };
    }

    async beforeRead(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.isElementCompressed(element)) {
            await this.decompressElement(element);
        }
        return { success: true };
    }

    // Основные методы сжатия
    async compressData(
        data: Buffer | string, 
        options: Partial<CompressionOptions> = {}
    ): Promise<CompressionResult> {
        const startTime = Date.now();
        const inputBuffer = Buffer.isBuffer(data) ? data : Buffer.from(data, 'utf8');
        const originalSize = inputBuffer.length;

        const compressionOptions: CompressionOptions = {
            algorithm: this.config.defaultAlgorithm,
            level: this.config.defaultLevel,
            validateChecksum: this.config.validateChecksums,
            ...options
        };

        // Проверка кэша
        const cacheKey = this.generateCacheKey(inputBuffer, compressionOptions);
        if (this.config.enableCaching) {
            const cached = this.getCachedCompression(cacheKey);
            if (cached) {
                this.stats.cacheHits++;
                return this.createResultFromCache(cached, originalSize, startTime);
            }
            this.stats.cacheMisses++;
        }

        try {
            // Выполнение сжатия
            const compressedData = await this.performCompression(inputBuffer, compressionOptions);
            const compressedSize = compressedData.length;
            const compressionRatio = compressedSize / originalSize;
            const duration = Date.now() - startTime;

            // Вычисление контрольной суммы
            const checksum = this.calculateChecksum(compressedData);

            const result: CompressionResult = {
                originalSize,
                compressedSize,
                compressionRatio,
                algorithm: compressionOptions.algorithm,
                level: compressionOptions.level,
                duration,
                checksum,
                metadata: {
                    inputType: Buffer.isBuffer(data) ? 'buffer' : 'string',
                    timestamp: new Date().toISOString()
                }
            };

            // Сохранение в кэш
            if (this.config.enableCaching) {
                this.cacheCompression(cacheKey, compressedData, compressionOptions, checksum);
            }

            // Обновление статистики
            this.updateCompressionStats(result);

            return result;

        } catch (error) {
            this.stats.failedCompressions++;
            throw new Error(`Ошибка сжатия: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    async decompressData(
        compressedData: Buffer, 
        algorithm: CompressionAlgorithm,
        expectedChecksum?: string
    ): Promise<Buffer> {
        const startTime = Date.now();

        try {
            // Валидация контрольной суммы
            if (expectedChecksum && this.config.validateChecksums) {
                const actualChecksum = this.calculateChecksum(compressedData);
                if (actualChecksum !== expectedChecksum) {
                    throw new Error('Нарушена целостность сжатых данных');
                }
            }

            // Выполнение декомпрессии
            const decompressedData = await this.performDecompression(compressedData, algorithm);
            const duration = Date.now() - startTime;

            // Обновление статистики
            this.stats.totalDecompressions++;
            this.stats.averageDecompressionTime = 
                (this.stats.averageDecompressionTime + duration) / 2;

            return decompressedData;

        } catch (error) {
            throw new Error(`Ошибка декомпрессии: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    async compressElement(element: CSElement): Promise<CompressionResult | null> {
        if (!this.shouldCompress(element)) {
            return null;
        }

        const jobId = this.generateId();
        const serializedData = this.serializeElementForCompression(element);
        const dataBuffer = Buffer.from(serializedData, 'utf8');

        const job: CompressionJob = {
            id: jobId,
            elementId: element.id,
            status: 'pending',
            algorithm: this.config.defaultAlgorithm,
            level: this.config.defaultLevel,
            startTime: new Date(),
            originalSize: dataBuffer.length
        };

        this.jobs.set(jobId, job);
        this.stats.activeJobs++;

        try {
            job.status = 'processing';

            // Выбор алгоритма и уровня сжатия
            const rule = this.findMatchingRule(element);
            const algorithm = rule?.algorithm || this.config.defaultAlgorithm;
            const level = rule?.level || this.config.defaultLevel;

            const options: CompressionOptions = {
                algorithm,
                level,
                validateChecksum: this.config.validateChecksums,
                ...rule?.options
            };

            // Сжатие данных
            const result = await this.compressData(dataBuffer, options);

            // Сохранение результата в элементе
            element.setData('_compressed', true);
            element.setData('_compressionResult', result);
            element.setData('_originalData', serializedData);

            job.status = 'completed';
            job.endTime = new Date();
            job.compressedSize = result.compressedSize;
            job.result = result;

            this.compressedElements.set(element.id, result);

            console.log(`Элемент ${element.id} сжат: ${result.originalSize} -> ${result.compressedSize} байт (${(result.compressionRatio * 100).toFixed(1)}%)`);

            return result;

        } catch (error) {
            job.status = 'failed';
            job.error = error instanceof Error ? error.message : String(error);
            job.endTime = new Date();
            
            console.error(`Ошибка сжатия элемента ${element.id}:`, error);
            return null;
        } finally {
            this.stats.activeJobs--;
            // Удаление задачи через 5 минут
            setTimeout(() => {
                this.jobs.delete(jobId);
            }, 300000);
        }
    }

    async decompressElement(element: CSElement): Promise<boolean> {
        if (!this.isElementCompressed(element)) {
            return true;
        }

        try {
            const compressionResult = element.getData('_compressionResult') as CompressionResult;
            const originalData = element.getData('_originalData') as string;

            if (!compressionResult || !originalData) {
                console.error(`Отсутствуют данные для декомпрессии элемента ${element.id}`);
                return false;
            }

            // Восстановление оригинальных данных
            const parsedData = JSON.parse(originalData);
            
            // Восстановление данных элемента
            Object.keys(parsedData).forEach(key => {
                if (!key.startsWith('_compression')) {
                    element.setData(key, parsedData[key]);
                }
            });

            // Удаление данных сжатия
            element.setData('_compressed', false);
            element.setData('_compressionResult', undefined);
            element.setData('_originalData', undefined);

            this.compressedElements.delete(element.id);

            console.log(`Элемент ${element.id} декомпрессирован`);
            return true;

        } catch (error) {
            console.error(`Ошибка декомпрессии элемента ${element.id}:`, error);
            return false;
        }
    }

    // Управление профилями
    createProfile(profileData: Omit<CompressionProfile, 'id'>): CompressionProfile {
        const profile: CompressionProfile = {
            id: this.generateId(),
            ...profileData
        };

        this.profiles.set(profile.id, profile);
        return profile;
    }

    getProfile(id: string): CompressionProfile | undefined {
        return this.profiles.get(id);
    }

    updateProfile(id: string, updates: Partial<CompressionProfile>): boolean {
        const profile = this.profiles.get(id);
        if (!profile) return false;

        Object.assign(profile, updates);
        return true;
    }

    deleteProfile(id: string): boolean {
        return this.profiles.delete(id);
    }

    // Управление правилами
    addRule(ruleData: Omit<CompressionRule, 'id' | 'createdAt' | 'updatedAt'>): CompressionRule {
        const rule: CompressionRule = {
            id: this.generateId(),
            createdAt: new Date(),
            updatedAt: new Date(),
            ...ruleData
        };

        this.rules.set(rule.id, rule);
        return rule;
    }

    getRule(id: string): CompressionRule | undefined {
        return this.rules.get(id);
    }

    updateRule(id: string, updates: Partial<CompressionRule>): boolean {
        const rule = this.rules.get(id);
        if (!rule) return false;

        Object.assign(rule, { ...updates, updatedAt: new Date() });
        return true;
    }

    deleteRule(id: string): boolean {
        return this.rules.delete(id);
    }

    // Бенчмаркинг
    async runBenchmark(
        data: Buffer, 
        algorithms: CompressionAlgorithm[] = Object.values(CompressionAlgorithm) as CompressionAlgorithm[],
        levels: CompressionLevel[] = Object.values(CompressionLevel) as CompressionLevel[]
    ): Promise<BenchmarkResult[]> {
        if (!this.config.enableBenchmarking) {
            throw new Error('Бенчмаркинг отключен');
        }

        const results: BenchmarkResult[] = [];
        const dataType = this.detectDataType(data);

        for (const algorithm of algorithms) {
            for (const level of levels) {
                try {
                    const compressionStart = Date.now();
                    const compressionResult = await this.compressData(data, { algorithm, level });
                    const compressionTime = Date.now() - compressionStart;

                    const decompressionStart = Date.now();
                    await this.decompressData(
                        Buffer.from('compressed_data'), // заглушка
                        algorithm
                    );
                    const decompressionTime = Date.now() - decompressionStart;

                    const result: BenchmarkResult = {
                        dataSize: data.length,
                        dataType,
                        algorithm,
                        level,
                        compressionRatio: compressionResult.compressionRatio,
                        compressionTime,
                        decompressionTime,
                        memoryUsage: process.memoryUsage().heapUsed,
                        timestamp: new Date()
                    };

                    results.push(result);

                } catch (error) {
                    console.error(`Ошибка бенчмарка ${algorithm}/${level}:`, error);
                }
            }
        }

        return results.sort((a, b) => a.compressionRatio - b.compressionRatio);
    }

    // Анализ и оптимизация
    async analyzeBestAlgorithm(data: Buffer): Promise<CompressionAlgorithm> {
        const benchmarkResults = await this.runBenchmark(data);
        
        if (benchmarkResults.length === 0) {
            return this.config.defaultAlgorithm;
        }

        // Выбор лучшего алгоритма по соотношению сжатия и скорости
        const scored = benchmarkResults.map(result => ({
            algorithm: result.algorithm,
            score: (1 - result.compressionRatio) * 0.7 + (1 / result.compressionTime) * 0.3
        }));

        scored.sort((a, b) => b.score - a.score);
        return scored[0].algorithm;
    }

    getCompressionRecommendations(element: CSElement): {
        algorithm: CompressionAlgorithm;
        level: CompressionLevel;
        expectedRatio: number;
        reason: string;
    } {
        const dataSize = this.serializeElementForCompression(element).length;
        const dataType = this.detectElementDataType(element);

        // Рекомендации на основе типа и размера данных
        if (dataSize < 1024) {
            return {
                algorithm: CompressionAlgorithm.LZ4,
                level: CompressionLevel.FAST,
                expectedRatio: 0.8,
                reason: 'Малый размер данных - приоритет скорости'
            };
        }

        if (dataType === DataType.TEXT || dataType === DataType.JSON) {
            return {
                algorithm: CompressionAlgorithm.BROTLI,
                level: CompressionLevel.BALANCED,
                expectedRatio: 0.4,
                reason: 'Текстовые данные хорошо сжимаются Brotli'
            };
        }

        return {
            algorithm: this.config.defaultAlgorithm,
            level: this.config.defaultLevel,
            expectedRatio: 0.6,
            reason: 'Стандартные настройки'
        };
    }

    // Утилиты
    /**
     * Сериализация элемента для сжатия с использованием встроенной сериализации
     */
    private serializeElementForCompression(element: CSElement): string {
        // Используем встроенную сериализацию CSElement
        const serialized = element.serialize({
            includeChildren: true,
            includeData: true,
            includeMetadata: true
        });
        
        return JSON.stringify(serialized);
    }

    private shouldCompress(element: CSElement): boolean {
        if (!this.config.autoCompress) return false;

        const serializedData = this.serializeElementForCompression(element);
        const dataSize = Buffer.byteLength(serializedData, 'utf8');

        // Проверка минимального размера
        if (dataSize < this.config.compressionThreshold) {
            return false;
        }

        // Проверка лимита задач
        if (this.stats.activeJobs >= this.config.maxCompressionJobs) {
            return false;
        }

        // Проверка типов данных
        const dataType = this.detectElementDataType(element);
        
        if (this.config.blacklistedDataTypes.includes(dataType)) {
            return false;
        }

        if (this.config.whitelistedDataTypes.length > 0 && 
            !this.config.whitelistedDataTypes.includes(dataType)) {
            return false;
        }

        return true;
    }

    private isElementCompressed(element: CSElement): boolean {
        return element.getData('_compressed') === true;
    }

    private findMatchingRule(element: CSElement): CompressionRule | undefined {
        const rules = Array.from(this.rules.values())
            .filter(rule => rule.enabled)
            .sort((a, b) => b.priority - a.priority);

        for (const rule of rules) {
            if (this.evaluateRuleConditions(rule, element)) {
                return rule;
            }
        }

        return undefined;
    }

    private evaluateRuleConditions(rule: CompressionRule, element: CSElement): boolean {
        return rule.conditions.every(condition => {
            switch (condition.type) {
                case 'size':
                    const size = this.serializeElementForCompression(element).length;
                    return this.evaluateCondition(size, condition.operator, condition.value);
                
                case 'elementType':
                    const elementType = element.constructor.name;
                    return this.evaluateCondition(elementType, condition.operator, condition.value);
                
                case 'dataType':
                    const dataType = this.detectElementDataType(element);
                    return this.evaluateCondition(dataType, condition.operator, condition.value);
                
                default:
                    return true;
            }
        });
    }

    private evaluateCondition(actual: any, operator: string, expected: any): boolean {
        switch (operator) {
            case 'equals': return actual === expected;
            case 'greater': return actual > expected;
            case 'less': return actual < expected;
            case 'contains': return String(actual).includes(String(expected));
            case 'matches': return new RegExp(expected).test(String(actual));
            default: return true;
        }
    }

    private detectElementDataType(element: CSElement): DataType {
        const serialized = this.serializeElementForCompression(element);
        
        // Простая эвристика определения типа данных
        if (serialized.includes('"image"') || serialized.includes('data:image/')) {
            return DataType.IMAGE;
        }
        
        if (serialized.includes('"audio"') || serialized.includes('data:audio/')) {
            return DataType.AUDIO;
        }
        
        if (serialized.includes('"video"') || serialized.includes('data:video/')) {
            return DataType.VIDEO;
        }
        
        try {
            JSON.parse(serialized);
            return DataType.JSON;
        } catch {
            return DataType.TEXT;
        }
    }

    private detectDataType(data: Buffer): DataType {
        // Проверка магических байтов для определения типа
        const header = data.slice(0, 16);
        
        // JPEG
        if (header[0] === 0xFF && header[1] === 0xD8) return DataType.IMAGE;
        // PNG
        if (header.slice(0, 8).equals(Buffer.from([0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]))) return DataType.IMAGE;
        // PDF
        if (header.slice(0, 4).equals(Buffer.from('%PDF'))) return DataType.BINARY;
        
        // Проверка на текст
        const text = data.toString('utf8', 0, Math.min(1024, data.length));
        try {
            JSON.parse(text);
            return DataType.JSON;
        } catch {
            // Проверка на читаемый текст
            const printableChars = text.replace(/[^\x20-\x7E]/g, '').length;
            const ratio = printableChars / text.length;
            return ratio > 0.8 ? DataType.TEXT : DataType.BINARY;
        }
    }

    private async performCompression(data: Buffer, options: CompressionOptions): Promise<Buffer> {
        try {
            switch (options.algorithm) {
                case CompressionAlgorithm.GZIP: {
                    const level = Math.max(0, Math.min(9, options.level)) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
                    const compressed = pako.gzip(data, { level });
                    return Buffer.from(compressed);
                }
                
                case CompressionAlgorithm.DEFLATE: {
                    const level = Math.max(0, Math.min(9, options.level)) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
                    const compressed = pako.deflate(data, { level });
                    return Buffer.from(compressed);
                }
                
                case CompressionAlgorithm.LZ4: {
                    const compressed = lz4.compress(data);
                    return Buffer.from(compressed);
                }
                
                case CompressionAlgorithm.BROTLI:
                    // Brotli требует дополнительной настройки, пока используем gzip как fallback
                    console.warn('Brotli сжатие пока не поддерживается, используется GZIP');
                    const gzipLevel = Math.max(0, Math.min(9, options.level)) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
                    const gzipCompressed = pako.gzip(data, { level: gzipLevel });
                    return Buffer.from(gzipCompressed);
                
                default:
                    throw new Error(`Неподдерживаемый алгоритм сжатия: ${options.algorithm}`);
            }
        } catch (error) {
            console.error('Ошибка сжатия данных:', error);
            // В случае ошибки возвращаем исходные данные
            return data;
        }
    }

    private async performDecompression(data: Buffer, algorithm: CompressionAlgorithm): Promise<Buffer> {
        try {
            switch (algorithm) {
                case CompressionAlgorithm.GZIP: {
                    const decompressed = pako.ungzip(data);
                    return Buffer.from(decompressed);
                }
                
                case CompressionAlgorithm.DEFLATE: {
                    const decompressed = pako.inflate(data);
                    return Buffer.from(decompressed);
                }
                
                case CompressionAlgorithm.LZ4: {
                    const decompressed = lz4.decompress(data);
                    return Buffer.from(decompressed);
                }
                
                case CompressionAlgorithm.BROTLI:
                    // Brotli декомпрессия пока не поддерживается, используем gzip как fallback
                    console.warn('Brotli декомпрессия пока не поддерживается, используется GZIP');
                    const gzipDecompressed = pako.ungzip(data);
                    return Buffer.from(gzipDecompressed);
                
                default:
                    throw new Error(`Неподдерживаемый алгоритм декомпрессии: ${algorithm}`);
            }
        } catch (error) {
            console.error('Ошибка декомпрессии данных:', error);
            // В случае ошибки возвращаем исходные данные
            return data;
        }
    }

    private generateCacheKey(data: Buffer, options: CompressionOptions): string {
        const dataHash = this.calculateChecksum(data);
        const optionsHash = this.calculateChecksum(Buffer.from(JSON.stringify(options)));
        return `${dataHash}-${optionsHash}`;
    }

    private getCachedCompression(key: string): CompressionCache | undefined {
        const cached = this.cache.get(key);
        if (!cached) return undefined;

        // Проверка TTL
        if (Date.now() - cached.createdAt.getTime() > this.config.cacheTTL) {
            this.cache.delete(key);
            return undefined;
        }

        cached.lastAccessed = new Date();
        cached.accessCount++;
        return cached;
    }

    private cacheCompression(
        key: string, 
        data: Buffer, 
        options: CompressionOptions, 
        checksum: string
    ): void {
        // Проверка лимита размера кэша
        if (this.getCurrentCacheSize() + data.length > this.config.cacheSize) {
            this.evictLeastRecentlyUsed();
        }

        const cacheEntry: CompressionCache = {
            key,
            data,
            algorithm: options.algorithm,
            level: options.level,
            checksum,
            createdAt: new Date(),
            lastAccessed: new Date(),
            accessCount: 1,
            size: data.length
        };

        this.cache.set(key, cacheEntry);
    }

    private createResultFromCache(
        cached: CompressionCache, 
        originalSize: number, 
        startTime: number
    ): CompressionResult {
        return {
            originalSize,
            compressedSize: cached.size,
            compressionRatio: cached.size / originalSize,
            algorithm: cached.algorithm,
            level: cached.level,
            duration: Date.now() - startTime,
            checksum: cached.checksum,
            metadata: {
                fromCache: true,
                cacheHit: true
            }
        };
    }

    private cleanupCache(): void {
        const now = Date.now();
        const toDelete: string[] = [];

        for (const [key, entry] of this.cache.entries()) {
            if (now - entry.createdAt.getTime() > this.config.cacheTTL) {
                toDelete.push(key);
            }
        }

        toDelete.forEach(key => this.cache.delete(key));
        
        if (toDelete.length > 0) {
            console.log(`Удалено ${toDelete.length} устаревших записей из кэша сжатия`);
        }
    }

    private evictLeastRecentlyUsed(): void {
        const entries = Array.from(this.cache.entries());
        entries.sort((a, b) => a[1].lastAccessed.getTime() - b[1].lastAccessed.getTime());
        
        // Удаляем 25% наименее используемых записей
        const toEvict = Math.ceil(entries.length * 0.25);
        for (let i = 0; i < toEvict; i++) {
            this.cache.delete(entries[i][0]);
        }
    }

    private getCurrentCacheSize(): number {
        return Array.from(this.cache.values()).reduce((total, entry) => total + entry.size, 0);
    }

    private updateCompressionStats(result: CompressionResult): void {
        this.stats.totalCompressions++;
        this.stats.totalOriginalSize += result.originalSize;
        this.stats.totalCompressedSize += result.compressedSize;
        this.stats.savedSpace += (result.originalSize - result.compressedSize);
        
        this.stats.averageCompressionRatio = this.stats.totalCompressedSize / this.stats.totalOriginalSize;
        this.stats.averageCompressionTime = (this.stats.averageCompressionTime + result.duration) / 2;
        
        this.stats.compressionsByAlgorithm[result.algorithm]++;
        this.stats.compressionsByLevel[result.level]++;
        
        this.stats.lastCompressionTime = new Date();
        
        if (result.compressionRatio > this.stats.bestCompressionRatio) {
            this.stats.bestCompressionRatio = result.compressionRatio;
        }
        
        if (result.compressionRatio < this.stats.worstCompressionRatio) {
            this.stats.worstCompressionRatio = result.compressionRatio;
        }
    }

    private updateStats(): void {
        if (this.stats.cacheHits + this.stats.cacheMisses > 0) {
            this.stats.cacheHitRatio = this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses);
        }
    }

    private initializeDefaultProfiles(): void {
        // Профиль для текстовых данных
        this.createProfile({
            name: 'Text Optimized',
            description: 'Оптимизирован для текстовых данных',
            dataTypes: [DataType.TEXT, DataType.JSON],
            algorithm: CompressionAlgorithm.BROTLI,
            level: CompressionLevel.BALANCED,
            options: {
                algorithm: CompressionAlgorithm.BROTLI,
                level: CompressionLevel.BALANCED,
                validateChecksum: true
            },
            isDefault: true
        });

        // Профиль для бинарных данных
        this.createProfile({
            name: 'Binary Optimized',
            description: 'Оптимизирован для бинарных данных',
            dataTypes: [DataType.BINARY, DataType.IMAGE],
            algorithm: CompressionAlgorithm.LZ4,
            level: CompressionLevel.FAST,
            options: {
                algorithm: CompressionAlgorithm.LZ4,
                level: CompressionLevel.FAST,
                validateChecksum: true
            },
            isDefault: false
        });
    }

    private initializeDefaultRules(): void {
        // Правило для больших данных
        this.addRule({
            name: 'Large Data',
            description: 'Максимальное сжатие для больших данных',
            enabled: true,
            priority: 100,
            conditions: [{
                type: 'size',
                operator: 'greater',
                value: 1024 * 1024 // 1MB
            }],
            algorithm: CompressionAlgorithm.BROTLI,
            level: CompressionLevel.BEST
        });

        // Правило для быстрого сжатия
        this.addRule({
            name: 'Fast Compression',
            description: 'Быстрое сжатие для небольших данных',
            enabled: true,
            priority: 50,
            conditions: [{
                type: 'size',
                operator: 'less',
                value: 10240 // 10KB
            }],
            algorithm: CompressionAlgorithm.LZ4,
            level: CompressionLevel.FASTEST
        });
    }

    private calculateChecksum(data: Buffer): string {
        // Простая контрольная сумма
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            hash = ((hash << 5) - hash) + data[i];
            hash = hash & hash;
        }
        return hash.toString(16);
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // Публичные методы для получения данных
    getStats(): CompressionStats {
        return { ...this.stats };
    }

    getAllProfiles(): CompressionProfile[] {
        return Array.from(this.profiles.values());
    }

    getAllRules(): CompressionRule[] {
        return Array.from(this.rules.values());
    }

    getActiveJobs(): CompressionJob[] {
        return Array.from(this.jobs.values()).filter(job => job.status === 'processing');
    }

    getCompressedElements(): Map<string, CompressionResult> {
        return new Map(this.compressedElements);
    }

    getCacheInfo(): {
        size: number;
        entries: number;
        hitRatio: number;
        totalSize: number;
    } {
        return {
            size: this.cache.size,
            entries: this.cache.size,
            hitRatio: this.stats.cacheHitRatio,
            totalSize: this.getCurrentCacheSize()
        };
    }
} 